import { Currency, CurrencyAmount } from '@kodiak-finance/sdk-core'
import CHARM_PERIPHERY_ABI from 'abis/CharmPeriphery.json'
import CHARM_VAULT_ABI from 'abis/CharmVault.json'
import { REFETCH_POLICY_FAST, WAGMI_REFETCH_POLICY_NEVER } from 'config/RefetchPolicies'
import { CHARM_PERIPHERY_ADDRESSES } from 'constants/addresses'
import { useToken } from 'hooks/Tokens'
import { useConnection } from 'hooks/useConnection'
import JSBI from 'jsbi'
import { useCurrencyBalances } from 'lib/hooks/useCurrencyBalance'
import tryParseCurrencyAmount from 'lib/utils/tryParseCurrencyAmount'
import { ReactNode, useCallback, useMemo } from 'react'
import { AppState } from 'state'
import { useAppDispatch, useAppSelector } from 'state/hooks'
import { Abi, Address } from 'viem'

import { useReadContractMultiple } from '../../../hooks/useReadContractMultiple'
import { Field, resetMintState, setDeadline, setSlippageTolerance, switchFields, typeInput } from './actions'

export function useMintCharmState(): AppState['mintCharm'] {
  return useAppSelector((state) => state.mintCharm)
}

export function useCharmVaultTokens(vaultAddress?: Address) {
  const { data } = useReadContractMultiple({
    contracts: [
      {
        address: vaultAddress,
        abi: CHARM_VAULT_ABI as Abi,
        functionName: 'token0',
      },
      {
        address: vaultAddress,
        abi: CHARM_VAULT_ABI as Abi,
        functionName: 'token1',
      },
      {
        address: vaultAddress,
        abi: CHARM_VAULT_ABI as Abi,
        functionName: 'decimals',
      },
    ],
    query: {
      ...WAGMI_REFETCH_POLICY_NEVER,
      enabled: !!vaultAddress,
    },
  })

  const token0Address = data?.[0]?.result as string
  const token1Address = data?.[1]?.result as string
  const vaultDecimals = data?.[2]?.result as number

  const token0 = useToken(token0Address)
  const token1 = useToken(token1Address)

  return { token0, token1, vaultDecimals }
}

export function useMintCharmActionHandlers(): {
  onFieldAInput: (typedValue: string) => void
  onFieldBInput: (typedValue: string) => void
  onSwitchField: (independentField: Field, typedValue: string, otherTypedValue: string) => void
  onSetSlippageTolerance: (slippageTolerance: number) => void
  onSetDeadline: (deadline: number) => void
  onResetMintState: () => void
} {
  const dispatch = useAppDispatch()

  const onFieldAInput = useCallback(
    (typedValue: string) => {
      dispatch(typeInput({ field: Field.CURRENCY_A, typedValue, noLiquidity: false }))
    },
    [dispatch]
  )

  const onFieldBInput = useCallback(
    (typedValue: string) => {
      dispatch(typeInput({ field: Field.CURRENCY_B, typedValue, noLiquidity: false }))
    },
    [dispatch]
  )

  const onSwitchField = useCallback(
    (independentField: Field, typedValue: string, otherTypedValue: string) => {
      dispatch(switchFields({ independentField, typedValue, otherTypedValue }))
    },
    [dispatch]
  )

  const onSetSlippageTolerance = useCallback(
    (slippageTolerance: number) => {
      dispatch(setSlippageTolerance({ slippageTolerance }))
    },
    [dispatch]
  )

  const onSetDeadline = useCallback(
    (deadline: number) => {
      dispatch(setDeadline({ deadline }))
    },
    [dispatch]
  )

  const onResetMintState = useCallback(() => {
    dispatch(resetMintState())
  }, [dispatch])

  return {
    onFieldAInput,
    onFieldBInput,
    onSwitchField,
    onSetSlippageTolerance,
    onSetDeadline,
    onResetMintState,
  }
}

export function useDerivedMintCharmInfo(
  vaultAddress: Address | undefined,
  currencyA: Currency | undefined,
  currencyB: Currency | undefined
): {
  dependentField: Field
  currencies: { [field in Field]?: Currency }
  currencyBalances: { [field in Field]?: CurrencyAmount<Currency> }
  parsedAmounts: { [field in Field]?: CurrencyAmount<Currency> }
  expectedShares?: CurrencyAmount<Currency>
  expectedSharesMin?: CurrencyAmount<Currency>
  error?: ReactNode
  amount0Min?: CurrencyAmount<Currency>
  amount1Min?: CurrencyAmount<Currency>
  noLiquidity?: boolean
  dependentAmountFetching: boolean
  vaultTotalSupply?: JSBI
  vaultAmount0?: JSBI
  vaultAmount1?: JSBI
} {
  const { account, chainId } = useConnection()
  const { independentField, typedValue, otherTypedValue } = useMintCharmState()
  const dependentField = useMemo(
    () => (independentField === Field.CURRENCY_A ? Field.CURRENCY_B : Field.CURRENCY_A),
    [independentField]
  )

  const { token0, token1 } = useCharmVaultTokens(vaultAddress)
  const peripheryAddress = CHARM_PERIPHERY_ADDRESSES[chainId]

  // Get the actual vault token (LP token)
  const vaultToken = useToken(vaultAddress)

  const currencies: { [field in Field]?: Currency } = useMemo(
    () => ({
      [Field.CURRENCY_A]: currencyA,
      [Field.CURRENCY_B]: currencyB,
    }),
    [currencyA, currencyB]
  )

  const currenciesValid = useMemo(() => {
    if (!token0 || !token1 || !currencyA || !currencyB) return false
    const addressA = currencyA.isNative ? currencyA.wrapped.address : currencyA.address
    const addressB = currencyB.isNative ? currencyB.wrapped.address : currencyB.address
    return (
      (addressA === token0.address && addressB === token1.address) ||
      (addressA === token1.address && addressB === token0.address)
    )
  }, [token0, token1, currencyA, currencyB])

  const currencyBalances = useCurrencyBalances(
    account ?? undefined,
    useMemo(() => [currencies[Field.CURRENCY_A], currencies[Field.CURRENCY_B]], [currencies]),
    REFETCH_POLICY_FAST
  )

  const independentAmount = useMemo(
    () => tryParseCurrencyAmount(typedValue, currencies[independentField]),
    [typedValue, currencies, independentField]
  )

  const vaultContracts = useMemo(() => {
    if (!peripheryAddress || !vaultAddress) return []
    return [
      {
        address: peripheryAddress,
        abi: CHARM_PERIPHERY_ABI as Abi,
        functionName: 'getVaultAmounts',
        args: [vaultAddress],
      },
      {
        address: vaultAddress,
        abi: CHARM_VAULT_ABI as Abi,
        functionName: 'totalSupply',
      },
    ]
  }, [peripheryAddress, vaultAddress])

  const { data: vaultResults, isLoading: vaultAmountsFetching } = useReadContractMultiple({
    contracts: vaultContracts,
    query: {
      ...REFETCH_POLICY_FAST,
      enabled: vaultContracts.length > 0,
    },
  })

  const vaultAmounts = vaultResults?.[0]?.result as [bigint, bigint] | undefined
  const vaultAmount0 = vaultAmounts?.[0] ? JSBI.BigInt(vaultAmounts[0].toString()) : undefined
  const vaultAmount1 = vaultAmounts?.[1] ? JSBI.BigInt(vaultAmounts[1].toString()) : undefined

  const vaultTotalSupply =
    vaultResults?.[1]?.result !== undefined ? JSBI.BigInt((vaultResults[1].result as bigint).toString()) : undefined

  const noLiquidity = useMemo(
    () => !vaultTotalSupply || JSBI.equal(vaultTotalSupply, JSBI.BigInt(0)),
    [vaultTotalSupply]
  )

  const dependentAmount = useMemo(() => {
    if (noLiquidity) {
      if (otherTypedValue && currencies[dependentField]) {
        return tryParseCurrencyAmount(otherTypedValue, currencies[dependentField])
      }
      return undefined
    }
    if (independentAmount && vaultAmount0 && vaultAmount1) {
      if (JSBI.equal(vaultAmount0, JSBI.BigInt(0)) || JSBI.equal(vaultAmount1, JSBI.BigInt(0))) return undefined
      const dependentCurrency = dependentField === Field.CURRENCY_B ? currencyB : currencyA
      const ratio =
        independentField === Field.CURRENCY_A
          ? JSBI.divide(JSBI.multiply(independentAmount.quotient, vaultAmount1), vaultAmount0)
          : JSBI.divide(JSBI.multiply(independentAmount.quotient, vaultAmount0), vaultAmount1)
      return dependentCurrency ? CurrencyAmount.fromRawAmount(dependentCurrency, ratio) : undefined
    }
    return undefined
  }, [
    noLiquidity,
    otherTypedValue,
    currencies,
    dependentField,
    independentAmount,
    vaultAmount0,
    vaultAmount1,
    currencyA,
    currencyB,
    independentField,
  ])

  const parsedAmounts = useMemo(
    () => ({
      [Field.CURRENCY_A]: independentField === Field.CURRENCY_A ? independentAmount : dependentAmount,
      [Field.CURRENCY_B]: independentField === Field.CURRENCY_A ? dependentAmount : independentAmount,
    }),
    [dependentAmount, independentAmount, independentField]
  )

  const amount0Min = useMemo(() => {
    const amount = parsedAmounts[Field.CURRENCY_A]
    if (!amount) return undefined
    return amount.multiply(99).divide(100)
  }, [parsedAmounts])

  const amount1Min = useMemo(() => {
    const amount = parsedAmounts[Field.CURRENCY_B]
    if (!amount) return undefined
    return amount.multiply(99).divide(100)
  }, [parsedAmounts])

  const expectedShares = useMemo(() => {
    if (
      !parsedAmounts[Field.CURRENCY_A] ||
      !parsedAmounts[Field.CURRENCY_B] ||
      !vaultTotalSupply ||
      !vaultAmount0 ||
      !vaultAmount1 ||
      !vaultToken
    ) {
      return undefined
    }

    const amount0 = parsedAmounts[Field.CURRENCY_A].quotient
    const amount1 = parsedAmounts[Field.CURRENCY_B].quotient
    const total0 = vaultAmount0
    const total1 = vaultAmount1
    const totalSupply = vaultTotalSupply

    if (JSBI.equal(totalSupply, JSBI.BigInt(0))) {
      const maxAmount = JSBI.greaterThan(amount0, amount1) ? amount0 : amount1
      return CurrencyAmount.fromRawAmount(vaultToken, JSBI.subtract(maxAmount, JSBI.BigInt(1000)))
    }

    let shares: JSBI

    if (JSBI.equal(total0, JSBI.BigInt(0)) && JSBI.equal(total1, JSBI.BigInt(0))) {
      const maxAmount = JSBI.greaterThan(amount0, amount1) ? amount0 : amount1
      shares = JSBI.subtract(maxAmount, JSBI.BigInt(1000))
    } else if (JSBI.equal(total0, JSBI.BigInt(0))) {
      shares = JSBI.divide(JSBI.multiply(amount1, totalSupply), total1)
    } else if (JSBI.equal(total1, JSBI.BigInt(0))) {
      shares = JSBI.divide(JSBI.multiply(amount0, totalSupply), total0)
    } else {
      const shares0 = JSBI.divide(JSBI.multiply(amount0, totalSupply), total0)
      const shares1 = JSBI.divide(JSBI.multiply(amount1, totalSupply), total1)
      shares = JSBI.lessThan(shares0, shares1) ? shares0 : shares1
    }

    return CurrencyAmount.fromRawAmount(vaultToken, shares)
  }, [parsedAmounts, vaultTotalSupply, vaultAmount0, vaultAmount1, vaultToken])

  const expectedSharesMin = useMemo(() => {
    if (!expectedShares) return undefined
    return expectedShares.multiply(99).divide(100)
  }, [expectedShares])

  // Validation and error checking
  let error: ReactNode | undefined
  if (!account) error = <>Connect Wallet</>
  if (!currenciesValid) error = error ?? <>Invalid currency pair for this vault</>
  if (!parsedAmounts[Field.CURRENCY_A] || !parsedAmounts[Field.CURRENCY_B]) error = error ?? <>Enter an amount</>

  const { [Field.CURRENCY_A]: currencyAAmount, [Field.CURRENCY_B]: currencyBAmount } = parsedAmounts
  if (currencyAAmount && currencyBalances[0]?.lessThan(currencyAAmount))
    error = <>Insufficient {currencies[Field.CURRENCY_A]?.symbol} balance</>
  if (currencyBAmount && currencyBalances[1]?.lessThan(currencyBAmount))
    error = <>Insufficient {currencies[Field.CURRENCY_B]?.symbol} balance</>

  return {
    dependentField,
    currencies,
    currencyBalances: {
      [Field.CURRENCY_A]: currencyBalances[0],
      [Field.CURRENCY_B]: currencyBalances[1],
    },
    parsedAmounts,
    expectedShares,
    expectedSharesMin,
    error,
    amount0Min,
    amount1Min,
    noLiquidity,
    dependentAmountFetching: vaultAmountsFetching,
    vaultTotalSupply,
    vaultAmount0,
    vaultAmount1,
  }
}
